import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { api, type HamperEntry } from '../api.js';
import { Button, Card, Empty, ErrorBox, Loading, Screen, formatDate, laundryGroupLabel} from '../ui.js';

/**
 * §3.4: Ein Waschgang ist ein Ereignis mit AUSWÄHLBARER Item-Menge — ein globaler Knopf
 * „dunkle Wäsche gewaschen" würde Teile sauber setzen, die noch gar nicht dran waren.
 *
 * D-36: Die Liste zeigt alles Waschbare, das zu Hause ist, nicht nur den Korb. Getragenes
 * ist vorausgewählt, Sauberes steht zur Wahl — Socken wäscht man mit, ohne sie je einzeln
 * gebucht zu haben. Der Zustand `in_hamper` wird beim Start mitgebucht; als Arbeitsschritt
 * war er fünf Wege über fünf Detailbildschirme, bevor eine Maschine startbar war.
 */
export function Laundry(): ReactNode {
  const qc = useQueryClient();
  const hamper = useQuery({ queryKey: ['hamper'], queryFn: api.hamper });
  const runs = useQuery({ queryKey: ['wash-runs'], queryFn: api.washRuns });
  const [group, setGroup] = useState<string | null>(null);
  // `null` heisst „noch nicht angefasst" — dann gilt die Vorauswahl nach Zustand.
  const [auswahl, setAuswahl] = useState<Set<string> | null>(null);

  const groups = useMemo(() => {
    const map = new Map<string, HamperEntry[]>();
    for (const entry of hamper.data ?? []) {
      const list = map.get(entry.laundry_group) ?? [];
      list.push(entry);
      map.set(entry.laundry_group, list);
    }
    return map;
  }, [hamper.data]);

  const activeGroup = group ?? [...groups.keys()][0] ?? null;
  const candidates = useMemo(
    () => (activeGroup === null ? [] : (groups.get(activeGroup) ?? [])),
    [groups, activeGroup],
  );
  // Getragen heisst: gehört in die Wäsche. Sauberes nur, wenn ausdrücklich angehakt.
  const vorauswahl = useMemo(
    () => new Set(candidates.filter((c) => c.lifecycle_state !== 'clean').map((c) => c.id)),
    [candidates],
  );
  const gewaehlt = auswahl ?? vorauswahl;
  const selected = candidates.filter((c) => gewaehlt.has(c.id));

  /**
   * D-37: Der niedrigste Wert der Auswahl gilt — wer ein 30-Grad-Teil bei 60 wäscht, hat
   * es einmal falsch gemacht und danach nicht mehr. `wash_temp_c` stand seit dem ersten
   * Schema in der Tabelle und wurde von nichts gelesen; der Waschgang trug immer NULL.
   */
  const [temp, setTemp] = useState<number | null | undefined>(undefined);
  const vorgeschlageneTemp = useMemo(() => {
    const werte = selected
      .map((s) => s.wash_temp_c)
      .filter((t): t is number => typeof t === 'number');
    return werte.length === 0 ? null : Math.min(...werte);
  }, [selected]);
  const tempWert = temp === undefined ? vorgeschlageneTemp : temp;

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['hamper'] });
    void qc.invalidateQueries({ queryKey: ['wash-runs'] });
    void qc.invalidateQueries({ queryKey: ['items'] });
  };

  const start = useMutation({
    mutationFn: () =>
      api.startWashRun({
        laundryGroup: activeGroup,
        itemIds: selected.map((s) => s.id),
        tempC: tempWert,
      }),
    onSuccess: () => {
      setAuswahl(null);
      setTemp(undefined);
      invalidate();
    },
  });

  const finish = useMutation({
    mutationFn: (id: string) => api.finishWashRun(id),
    onSuccess: invalidate,
  });

  const openRuns = (runs.data ?? []).filter((r) => r.finished_at === null);

  return (
    <Screen title="Wäsche">
      {openRuns.length > 0 && (
        <Card className="mb-4 ring-[#5e5ce6]/30">
          <p className="text-sm font-medium">Läuft gerade</p>
          {openRuns.map((r) => (
            <div key={r.id} className="mt-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm">
                  {laundryGroupLabel(r.laundry_group)} · {r.item_count} Teile
                </p>
                <p className="text-xs text-white/45">
                  {r.temp_c !== null ? `${r.temp_c} °C` : 'ohne Temperaturangabe'}
                </p>
              </div>
              <Button onClick={() => finish.mutate(r.id)} disabled={finish.isPending}>
                Fertig
              </Button>
            </div>
          ))}
          <p className="mt-3 text-xs text-white/45">
            „Fertig" heißt trocknen, nicht anziehbar — die Teile werden erst nach der
            Trockenzeit wieder vorgeschlagen.
          </p>
          <ErrorBox error={finish.error} />
        </Card>
      )}

      {hamper.isLoading && <Loading />}
      <ErrorBox error={hamper.error} />

      <IroningCard />

      {hamper.data !== undefined && hamper.data.length === 0 && (
        <Empty>Nichts Waschbares da.</Empty>
      )}

      {groups.size > 0 && (
        <>
          <div className="-mx-4 mb-4 overflow-x-auto px-4">
            <div className="flex gap-2 pb-1">
              {[...groups.entries()].map(([key, entries]) => {
                const getragen = entries.filter((e) => e.lifecycle_state !== 'clean').length;
                return (
                  <button
                    key={key}
                    onClick={() => {
                      setGroup(key);
                      setAuswahl(null);
                    }}
                    className={`shrink-0 rounded-full px-4 py-2 text-sm ${
                      activeGroup === key ? 'bg-white text-black' : 'bg-white/8 text-white/75'
                    }`}
                  >
                    {/* Getragen und insgesamt: „Dunkles (3/12)" sagt beides in einem Wort. */}
                    {laundryGroupLabel(key)} ({getragen}/{entries.length})
                  </button>
                );
              })}
            </div>
          </div>

          <Card>
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm text-white/55">
                Getragenes ist vorausgewählt. Sauberes lässt sich dazunehmen — der Weg in den
                Korb wird beim Start mitgebucht.
              </p>
              <button
                onClick={() => setAuswahl(new Set(gewaehlt.size === 0 ? candidates.map((c) => c.id) : []))}
                className="shrink-0 text-xs text-white/45 underline underline-offset-2"
              >
                {gewaehlt.size === 0 ? 'Alle' : 'Keine'}
              </button>
            </div>
            <div className="mt-3 space-y-2">
              {candidates.map((c) => {
                const on = gewaehlt.has(c.id);
                return (
                  <button
                    key={c.id}
                    onClick={() => {
                      const next = new Set(gewaehlt);
                      if (on) next.delete(c.id);
                      else next.add(c.id);
                      setAuswahl(next);
                    }}
                    className={`press flex w-full items-center justify-between gap-3 rounded-xl px-3 py-3 text-left text-sm ${
                      on ? 'bg-[var(--color-accent-soft)] text-white' : 'bg-white/8 text-white/45'
                    }`}
                  >
                    <span className="truncate">{c.name}</span>
                    <span className="shrink-0 text-xs">
                      {on
                        ? '✓ dabei'
                        : c.lifecycle_state === 'clean'
                          ? 'sauber'
                          : 'bleibt liegen'}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs text-white/55">Waschtemperatur</p>
                <p className="text-[11px] text-white/30">
                  {vorgeschlageneTemp === null
                    ? 'Kein Pflegeetikett erfasst — trag sie ein, wenn du magst.'
                    : `Niedrigster Wert der Auswahl: ${vorgeschlageneTemp} °C.`}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <input
                  value={tempWert ?? ''}
                  inputMode="numeric"
                  placeholder="—"
                  onChange={(e) => {
                    const n = Number.parseInt(e.target.value, 10);
                    setTemp(Number.isNaN(n) ? null : Math.min(95, Math.max(0, n)));
                  }}
                  className="tnum w-16 rounded-xl bg-white/6 px-2 py-2 text-center text-sm ring-1 ring-white/10 outline-none focus:ring-sky-500"
                />
                <span className="text-xs text-white/45">°C</span>
              </div>
            </div>

            <Button
              className="mt-3 w-full"
              onClick={() => start.mutate()}
              disabled={selected.length === 0 || start.isPending}
            >
              Waschgang mit {selected.length} {selected.length === 1 ? 'Teil' : 'Teilen'} starten
            </Button>
            <ErrorBox error={start.error} />
          </Card>
        </>
      )}

      <div className="mt-6">
        <CareCard />
      </div>
    </Screen>
  );
}

/**
 * §4: Nach dem Trocknen steht ein Hemd auf „zu bügeln" und wartet auf eine Bestätigung.
 * Die gab es nur einzeln über den Teil-Bildschirm — bei einer Ladung Hemden fünf Wege für
 * eine Handlung, die man ohnehin am Stück erledigt (D-36).
 */
function IroningCard(): ReactNode {
  const qc = useQueryClient();
  const offen = useQuery({
    queryKey: ['items', 'ironing'],
    queryFn: () => api.items({ state: 'ironing' }),
  });
  const buchen = useMutation({
    mutationFn: (ids: string[]) => api.bulkAction('ironed', ids),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['items'] });
      void qc.invalidateQueries({ queryKey: ['recommend'] });
    },
  });

  const teile = offen.data ?? [];
  if (teile.length === 0) return null;

  return (
    <Card className="mb-4">
      <p className="text-sm font-medium">Zu bügeln ({teile.length})</p>
      <p className="mt-1 text-xs text-white/45">
        Erst danach stehen die Teile wieder zur Verfügung. Einzeln antippen oder alles auf
        einmal buchen.
      </p>
      <div className="mt-3 space-y-2">
        {teile.map((t) => (
          <button
            key={t.id}
            onClick={() => buchen.mutate([t.id])}
            disabled={buchen.isPending}
            className="press flex w-full items-center justify-between gap-3 rounded-xl bg-white/8 px-3 py-3 text-left text-sm"
          >
            <span className="truncate">{t.name}</span>
            <span className="shrink-0 text-xs text-white/45">gebügelt</span>
          </button>
        ))}
      </div>
      <Button
        className="mt-4 w-full"
        onClick={() => buchen.mutate(teile.map((t) => t.id))}
        disabled={buchen.isPending}
      >
        {teile.length === 1 ? 'Gebügelt' : `Alle ${teile.length} gebügelt`}
      </Button>
      <ErrorBox error={offen.error ?? buchen.error} />
    </Card>
  );
}

/**
 * §9 Reinigung: Teile außer Haus. Der Zustand `at_care` ist bewusst kein Sonderfall des
 * Waschgangs — eine Reinigung dauert Tage, kostet Geld und hat einen Rückgabetermin,
 * den man vergisst. Genau dafür erinnert der `care_reminder`-Job.
 */
function CareCard(): ReactNode {
  const qc = useQueryClient();
  const open = useQuery({ queryKey: ['care-open'], queryFn: api.care.open });
  const items = useQuery({ queryKey: ['items'], queryFn: () => api.items({}) });
  const [picking, setPicking] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [vendor, setVendor] = useState('');
  const [back, setBack] = useState('');

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['care-open'] });
    void qc.invalidateQueries({ queryKey: ['items'] });
  };

  const send = useMutation({
    mutationFn: () =>
      api.care.send({
        itemIds: [...selected],
        type: 'dry_clean',
        vendor: vendor.trim() === '' ? null : vendor.trim(),
        expectedBackAt: back === '' ? null : back,
      }),
    onSuccess: () => {
      setSelected(new Set());
      setPicking(false);
      setVendor('');
      setBack('');
      invalidate();
    },
  });

  const returned = useMutation({
    mutationFn: (itemId: string) => api.action(itemId, 'care-returned'),
    onSuccess: invalidate,
  });

  // Nur was gerade zu Hause ist, kann weggegeben werden.
  const candidates = (items.data ?? []).filter((i) =>
    ['clean', 'worn_ok', 'soiled'].includes(i.lifecycleState),
  );

  return (
    <Card>
      <p className="text-sm font-medium">Reinigung</p>

      {(open.data ?? []).length === 0 ? (
        <p className="mt-1 text-xs text-white/45">Nichts außer Haus.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {(open.data ?? []).map((e) => (
            <li key={e.id} className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm">{e.item_name}</p>
                <p className="text-xs text-white/45">
                  {e.vendor ?? 'ohne Angabe'}
                  {e.expected_back_at !== null && ` · zurück ab ${formatDate(e.expected_back_at)}`}
                </p>
              </div>
              <Button
                variant="ghost"
                disabled={returned.isPending}
                onClick={() => returned.mutate(e.item_id)}
              >
                Zurück
              </Button>
            </li>
          ))}
        </ul>
      )}

      {picking ? (
        <div className="mt-4 border-t border-white/10 pt-4">
          <div className="max-h-64 space-y-1 overflow-y-auto">
            {candidates.map((i) => {
              const on = selected.has(i.id);
              return (
                <button
                  key={i.id}
                  onClick={() => {
                    const next = new Set(selected);
                    if (on) next.delete(i.id);
                    else next.add(i.id);
                    setSelected(next);
                  }}
                  className={`press flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm ${
                    on ? 'bg-[var(--color-accent-soft)] text-white' : 'bg-white/8 text-white/55'
                  }`}
                >
                  <span className="truncate">{i.name}</span>
                  {on && <span className="text-xs">✓</span>}
                </button>
              );
            })}
          </div>
          <input
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            placeholder="Reinigung (optional)"
            className="mt-3 w-full rounded-xl bg-white/6 px-3 py-2 text-sm ring-1 ring-white/10 outline-none focus:ring-sky-500"
          />
          <input
            type="date"
            value={back}
            onChange={(e) => setBack(e.target.value)}
            className="mt-2 w-full rounded-xl bg-white/6 px-3 py-2 text-sm ring-1 ring-white/10 outline-none focus:ring-sky-500"
          />
          <p className="mt-1 text-[11px] text-white/30">
            Mit Rückgabetermin erinnert die App, sobald er erreicht ist.
          </p>
          <div className="mt-3 flex gap-2">
            <Button
              className="flex-1"
              disabled={selected.size === 0 || send.isPending}
              onClick={() => send.mutate()}
            >
              {selected.size} Teile weggeben
            </Button>
            <Button variant="ghost" onClick={() => setPicking(false)}>
              Abbrechen
            </Button>
          </div>
          <ErrorBox error={send.error} />
        </div>
      ) : (
        <Button variant="ghost" className="mt-3 w-full" onClick={() => setPicking(true)}>
          Teile in die Reinigung geben
        </Button>
      )}
      <ErrorBox error={open.error ?? returned.error} />
    </Card>
  );
}
